import { StickyTypeEnum } from 'common/components/entities/Section/enums/StickyTypeEnum'
import { SectionInterface } from 'common/components/entities/Section/types/SectionInterface'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { EntitiesStructure } from 'common/types/Page'
import { rootEntitiesWithSections } from 'src/shared/constants/root-entities-with-sections'
import { DiagnosticFinding, DiagnosticRule } from '../types'

type DeviceType = 'desktop' | 'mobile'

function getSectionOrder(entities: EntitiesStructure): string[] {
  const body = Object.values(entities).find(e =>
    (rootEntitiesWithSections as ReadonlyArray<string>).includes(e.type),
  )

  if (!body || !('childIds' in body) || !Array.isArray(body.childIds)) {
    return []
  }

  return body.childIds as string[]
}

function createStickyFinding(
  section: SectionInterface,
  device: DeviceType,
  isFirst: boolean,
): DiagnosticFinding {
  const suffix = isFirst
    ? '(first sticky section)'
    : '(non-first — may cause layout issues)'

  return {
    ruleId: 'sticky-section',
    severity: isFirst ? 'info' : 'high',
    entityId: section.id,
    entityType: EntityTypeEnum.Section,
    message: `Section is sticky on ${device} ${suffix}`,
    metadata: { device, isFirst },
  }
}

function isStickySection(
  section: SectionInterface,
  device: DeviceType,
): boolean {
  const stickyProp =
    device === 'desktop' ? section.sticky : section.mobileSticky
  return stickyProp?.type === StickyTypeEnum.Sticky
}

export const stickySectionRule: DiagnosticRule = {
  id: 'sticky-section',
  name: 'Sticky Sections',
  run: (entities: EntitiesStructure): DiagnosticFinding[] => {
    const findings: DiagnosticFinding[] = []
    const sectionOrder = getSectionOrder(entities)

    const firstStickyFound: Record<DeviceType, boolean> = {
      desktop: false,
      mobile: false,
    }

    for (const sectionId of sectionOrder) {
      const entity = entities[sectionId]
      if (!entity || entity.type !== EntityTypeEnum.Section) {
        continue
      }
      const section = entity as SectionInterface

      for (const device of ['desktop', 'mobile'] as const) {
        if (isStickySection(section, device)) {
          const isFirst = !firstStickyFound[device]
          firstStickyFound[device] = true
          findings.push(createStickyFinding(section, device, isFirst))
        }
      }
    }

    return findings
  },
}
